import html2canvas from 'html2canvas';
import { saveFile, saveScreenshots } from '../../../../../services/saveScreenshot';
import { setItem } from '../../../../../helper/localStorageHelper';

// Enhanced configuration for higher quality images
const COMPRESSION_CONFIG = {
  maxWidth: 2560, // Increased from 1920
  maxHeight: 1440, // Increased from 1080
  quality: 0.92, // Increased from 0.8 for better quality
  format: 'image/jpeg', // JPEG for better compression than PNG
  targetSizeKB: 2048, // 2MB target
  scale: 2.0, // Increased from 1.5 for sharper images
  // Additional quality settings
  highQuality: true,
  usePNG: false, // Can be toggled for lossless quality if needed
  adaptiveQuality: true, // Adjust quality based on file size
};

export const handleDownload = async (viewRef) => {
  try {
    // Find the main map container that includes all overlays
    const mapContainer = findMapContainer();
    if (!mapContainer) {
      throw new Error('Map container not found');
    }

    // Ensure the ArcGIS view is ready
    if (!viewRef.current || !viewRef.current.ready) {
      throw new Error('ArcGIS view not ready');
    }

    // Wait for all animations and rendering to complete
    await new Promise((resolve) => setTimeout(resolve, 2000));

    let screenshotData = null;
    let methodUsed = '';

    // Method 1: Enhanced ArcGIS takeScreenshot approach
    try {
      screenshotData = await captureWithArcGISScreenshot(viewRef.current, mapContainer);
      if (screenshotData) {
        methodUsed = 'arcgis-enhanced';
      }
    } catch (error) {
      console.warn('ArcGIS screenshot method failed:', error);
    }

    // Method 2: html2canvas as fallback
    if (!screenshotData && typeof html2canvas !== 'undefined') {
      screenshotData = await captureWithHtml2Canvas(mapContainer);
      methodUsed = 'html2canvas';
    }

    // Method 3: Enhanced manual capture
    if (!screenshotData) {
      screenshotData = await captureManually(mapContainer, viewRef);
      methodUsed = 'manual-enhanced';
    }

    if (!screenshotData) {
      throw new Error('All screenshot methods failed');
    }

    // Process and save screenshots
    await processAndSaveScreenshots(screenshotData, methodUsed);
  } catch (error) {
    console.error('Screenshot failed:', error);
    alert(`Screenshot failed: ${error.message}`);
  }
};

export const handleCaptureAndReturn = async (viewRef) => {
  try {
    // Find the main map container that includes all overlays
    const mapContainer = findMapContainer();
    if (!mapContainer) {
      throw new Error('Map container not found');
    }

    // Ensure the ArcGIS view is ready
    if (!viewRef.current || !viewRef.current.ready) {
      throw new Error('ArcGIS view not ready');
    }

    // Wait for all animations and rendering to complete
    await new Promise((resolve) => setTimeout(resolve, 2000));

    let screenshotData = null;

    // Method 1: Enhanced ArcGIS takeScreenshot approach
    try {
      screenshotData = await captureWithArcGISScreenshot(viewRef.current, mapContainer);
    } catch (error) {
      console.warn('ArcGIS screenshot method failed:', error);
    }

    // Method 2: html2canvas as fallback
    if (!screenshotData && typeof html2canvas !== 'undefined') {
      screenshotData = await captureWithHtml2Canvas(mapContainer);
    }

    // Method 3: Enhanced manual capture
    if (!screenshotData) {
      screenshotData = await captureManually(mapContainer, viewRef);
    }

    if (!screenshotData) {
      throw new Error('All screenshot methods failed');
    }

    // Process and save screenshots

    const base64Data = await canvasToOptimizedBase64(screenshotData);
    return base64Data;
  } catch (error) {
    console.error('Screenshot failed:', error);
    alert(`Screenshot failed: ${error.message}`);
  }
};

// Helper function to find map container
const findMapContainer = () => {
  return (
    document.querySelector('.map-container') ||
    document.getElementById('arcgis-map-container')?.parentElement ||
    document.getElementById('arcgis-map-container')
  );
};

// Method 1: Enhanced ArcGIS takeScreenshot with overlay composition
const captureWithArcGISScreenshot = async (view, container) => {
  try {
    // Take base map screenshot with higher quality
    const baseScreenshot = await view.takeScreenshot({
      format: 'png',
      quality: 100,
      width: Math.min(view.width * 2, COMPRESSION_CONFIG.maxWidth), // Higher resolution
      height: Math.min(view.height * 2, COMPRESSION_CONFIG.maxHeight),
      ignorePadding: false,
    });

    // Create canvas for final composition
    const containerRect = container.getBoundingClientRect();
    const finalCanvas = createHighQualityCanvas(containerRect);

    const ctx = finalCanvas.getContext('2d');

    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Load and draw base map
    const baseImg = await loadImage(baseScreenshot.dataUrl);

    // Calculate map position within container
    const mapElement = document.getElementById('arcgis-map-container');
    const mapRect = mapElement.getBoundingClientRect();
    const offsetX = mapRect.left - containerRect.left;
    const offsetY = mapRect.top - containerRect.top;

    // Draw base map with high quality
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(baseImg, offsetX, offsetY, mapRect.width, mapRect.height);
    ctx.restore();

    // Capture and composite all overlay layers
    await captureOverlayLayers(ctx, container, containerRect);

    return finalCanvas;
  } catch (error) {
    console.error('Enhanced ArcGIS screenshot failed:', error);
    return null;
  }
};

// Method 2: Improved html2canvas with higher quality configuration
const captureWithHtml2Canvas = async (container) => {
  try {
    const containerRect = container.getBoundingClientRect();
    const canvas = await html2canvas(container, {
      useCORS: true,
      allowTaint: true,
      scale: COMPRESSION_CONFIG.scale, // Higher scale for better quality
      backgroundColor: '#ffffff',
      logging: false,
      height: containerRect.height,
      width: containerRect.width,
      scrollX: 0,
      scrollY: 0,
      foreignObjectRendering: false,
      // Enhanced quality settings
      removeContainer: false,
      async: true,
      imageTimeout: 15000, // Increased timeout for better quality
      ignoreElements: (element) => {
        // Keep all map-related elements
        if (
          element.closest('.map-container') ||
          element.closest('#arcgis-map-container') ||
          element.closest('.tool-controls-container')
        ) {
          return false;
        }
        // Ignore problematic UI elements
        return (
          element.classList.contains('esri-attribution') ||
          (element.classList.contains('esri-view-surface') && !element.querySelector('canvas'))
        );
      },
      onclone: (clonedDoc) => {
        const mapElements = clonedDoc.querySelectorAll('.map-container *, #arcgis-map-container *');
        mapElements.forEach((element) => {
          element.style.visibility = 'visible';
          element.style.display = element.style.display || 'block';
        });

        // Fix canvas elements with high quality
        const canvases = clonedDoc.querySelectorAll('canvas');
        canvases.forEach((canvas) => {
          canvas.style.display = 'block';
          canvas.style.visibility = 'visible';
          canvas.style.opacity = '1';
          // Ensure high-quality rendering
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
          }
        });

        // Fix SVG elements
        const svgs = clonedDoc.querySelectorAll('svg');
        svgs.forEach((svg) => {
          svg.style.display = 'block';
          svg.style.visibility = 'visible';
          // Enhance SVG quality
          svg.setAttribute('shape-rendering', 'crispEdges');
          svg.setAttribute('text-rendering', 'optimizeLegibility');
        });
      },
    });

    // Optimize canvas size while maintaining quality
    return optimizeCanvasQuality(canvas);
  } catch (error) {
    console.error('html2canvas method failed:', error);
    throw error;
  }
};

// Method 3: Enhanced manual capture with higher quality layer ordering
const captureManually = async (container, viewRef) => {
  const rect = container.getBoundingClientRect();

  // Create high-quality canvas
  const canvas = createHighQualityCanvas(rect);
  const ctx = canvas.getContext('2d');

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // White background
  ctx.fillStyle = 'white';
  ctx.fillRect(
    0,
    0,
    canvas.width / COMPRESSION_CONFIG.scale,
    canvas.height / COMPRESSION_CONFIG.scale
  );

  // Layer 1: ArcGIS base map
  await captureArcGISMap(ctx, viewRef, rect);

  // Layer 2: All overlay layers in correct order
  await captureOverlayLayers(ctx, container, rect);

  return canvas;
};

// Create high-quality canvas with enhanced settings
const createHighQualityCanvas = (rect) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Calculate high-quality dimensions
  let width = rect.width * COMPRESSION_CONFIG.scale;
  let height = rect.height * COMPRESSION_CONFIG.scale;

  // Limit maximum dimensions while maintaining quality
  if (width > COMPRESSION_CONFIG.maxWidth) {
    const ratio = COMPRESSION_CONFIG.maxWidth / width;
    width = COMPRESSION_CONFIG.maxWidth;
    height = height * ratio;
  }

  if (height > COMPRESSION_CONFIG.maxHeight) {
    const ratio = COMPRESSION_CONFIG.maxHeight / height;
    height = COMPRESSION_CONFIG.maxHeight;
    width = width * ratio;
  }

  canvas.width = width;
  canvas.height = height;

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.textRenderingOptimization = 'optimizeQuality';

  ctx.scale(width / rect.width, height / rect.height);

  return canvas;
};

// Optimize existing canvas quality without losing resolution
const optimizeCanvasQuality = (sourceCanvas) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  let width = sourceCanvas.width;
  let height = sourceCanvas.height;

  // Only reduce dimensions if they exceed maximum limits
  if (width > COMPRESSION_CONFIG.maxWidth || height > COMPRESSION_CONFIG.maxHeight) {
    const widthRatio = COMPRESSION_CONFIG.maxWidth / width;
    const heightRatio = COMPRESSION_CONFIG.maxHeight / height;
    const ratio = Math.min(widthRatio, heightRatio);

    width = Math.floor(width * ratio);
    height = Math.floor(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw with high-quality scaling
  ctx.drawImage(sourceCanvas, 0, 0, width, height);

  return canvas;
};

// Helper function to load images
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = 'anonymous'; // For better CORS handling
    img.src = src;
  });
};

// Enhanced ArcGIS map capture with higher quality
const captureArcGISMap = async (ctx, viewRef, containerRect) => {
  try {
    if (!viewRef.current || !viewRef.current.ready) {
      console.warn('ArcGIS view not ready');
      return;
    }

    // Force a refresh and wait
    await viewRef.current.when();
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Capture with higher resolution
    const screenshot = await viewRef.current.takeScreenshot({
      format: 'png',
      quality: 100,
      width: Math.min(viewRef.current.width * 1.5, COMPRESSION_CONFIG.maxWidth),
      height: Math.min(viewRef.current.height * 1.5, COMPRESSION_CONFIG.maxHeight),
      ignorePadding: false,
    });

    const img = await loadImage(screenshot.dataUrl);

    // Calculate precise positioning
    const mapElement = document.getElementById('arcgis-map-container');
    if (!mapElement) {
      console.warn('Map element not found');
      return;
    }

    const mapRect = mapElement.getBoundingClientRect();
    const offsetX = mapRect.left - containerRect.left;
    const offsetY = mapRect.top - containerRect.top;

    // Draw with high quality settings
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, offsetX, offsetY, mapRect.width, mapRect.height);
    ctx.restore();
  } catch (error) {
    console.error('Failed to capture ArcGIS base map:', error);
  }
};

// Enhanced overlay layer capture with improved quality
const captureOverlayLayers = async (ctx, container, containerRect) => {
  // Define layer capture order (back to front)
  const layerSelectors = [
    '.esri-view-surface canvas:not(.esri-view-surface > canvas:first-child)',
    'canvas[data-drawing]',
    'canvas[class*="architectural"]',
    'canvas[class*="shape"]',
    'svg',
    '.drawing-overlay svg',
    '.shape-overlay svg',
    '.architectural-overlay svg',
    '.image-overlay img',
    'img[data-imported]',
    '.tool-controls-container',
    '.measurement-overlay',
    '[class*="measurement"]',
    '[data-measurement]',
  ];

  // Capture each layer type in order with high quality
  for (const selector of layerSelectors) {
    try {
      await captureLayerBySelector(ctx, container, containerRect, selector);
    } catch (error) {
      console.warn(`Failed to capture layer ${selector}:`, error);
    }
  }

  // Special handling for ArcGIS graphics layers
  await captureArcGISGraphics(ctx, container, containerRect);
};

// Capture specific layer by selector
const captureLayerBySelector = async (ctx, container, containerRect, selector) => {
  const elements = container.querySelectorAll(selector);
  if (elements.length === 0) return;

  for (const element of elements) {
    try {
      await captureHighQualityElement(ctx, element, containerRect);
    } catch (error) {
      console.warn(`Failed to capture element:`, error);
    }
  }
};

// Enhanced element capture with high-quality handling for different types
const captureHighQualityElement = async (ctx, element, containerRect) => {
  const elementRect = element.getBoundingClientRect();

  if (elementRect.width <= 0 || elementRect.height <= 0) return;

  const offsetX = elementRect.left - containerRect.left;
  const offsetY = elementRect.top - containerRect.top;

  // Skip if completely outside container
  if (
    offsetX + elementRect.width < 0 ||
    offsetY + elementRect.height < 0 ||
    offsetX > containerRect.width ||
    offsetY > containerRect.height
  ) {
    return;
  }

  ctx.save();

  // Enable high-quality rendering for this element
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  try {
    if (element.tagName === 'CANVAS') {
      await captureHighQualityCanvas(ctx, element, offsetX, offsetY, elementRect);
    } else if (element.tagName === 'SVG') {
      await captureHighQualitySVG(ctx, element, offsetX, offsetY, elementRect);
    } else if (element.tagName === 'IMG') {
      await captureHighQualityImage(ctx, element, offsetX, offsetY, elementRect);
    } else {
      await captureGenericElement(ctx, element, offsetX, offsetY, elementRect);
    }
  } finally {
    ctx.restore();
  }
};

// Specialized high-quality canvas capture
const captureHighQualityCanvas = async (ctx, canvas, offsetX, offsetY, rect) => {
  if (canvas.width > 0 && canvas.height > 0) {
    try {
      ctx.globalAlpha = parseFloat(getComputedStyle(canvas).opacity) || 1;
      // Use high-quality scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(canvas, offsetX, offsetY, rect.width, rect.height);
    } catch (error) {
      console.warn('Canvas capture failed:', error);
    }
  }
};

// Enhanced high-quality SVG capture
const captureHighQualitySVG = async (ctx, svg, offsetX, offsetY, rect) => {
  try {
    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute('width', rect.width * 2); // Higher resolution
    svgClone.setAttribute('height', rect.height * 2);
    svgClone.setAttribute('viewBox', `0 0 ${rect.width * 2} ${rect.height * 2}`);

    // Enhance SVG quality attributes
    svgClone.setAttribute('shape-rendering', 'crispEdges');
    svgClone.setAttribute('text-rendering', 'optimizeLegibility');
    svgClone.setAttribute('color-rendering', 'optimizeQuality');

    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = await loadImage(url);

    if (img.complete && img.naturalWidth > 0) {
      ctx.globalAlpha = parseFloat(getComputedStyle(svg).opacity) || 1;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, offsetX, offsetY, rect.width, rect.height);
    }

    URL.revokeObjectURL(url);
  } catch (error) {
    console.warn('SVG capture failed:', error);
  }
};

// High-quality image capture
const captureHighQualityImage = async (ctx, img, offsetX, offsetY, rect) => {
  if (img.complete && img.naturalWidth > 0) {
    try {
      ctx.globalAlpha = parseFloat(getComputedStyle(img).opacity) || 1;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, offsetX, offsetY, rect.width, rect.height);
    } catch (error) {
      console.warn('Image capture failed:', error);
    }
  }
};

// Generic element capture (for UI elements) - unchanged
const captureGenericElement = async (ctx, element, offsetX, offsetY, rect) => {
  const computedStyle = getComputedStyle(element);

  // Draw background
  if (computedStyle.backgroundColor && computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)') {
    ctx.fillStyle = computedStyle.backgroundColor;
    ctx.fillRect(offsetX, offsetY, rect.width, rect.height);
  }

  // Draw border
  const borderWidth = parseFloat(computedStyle.borderWidth) || 0;
  if (borderWidth > 0) {
    ctx.strokeStyle = computedStyle.borderColor || '#000000';
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(offsetX, offsetY, rect.width, rect.height);
  }

  // Draw text content
  if (element.textContent && element.textContent.trim()) {
    ctx.fillStyle = computedStyle.color || '#000000';
    ctx.font = `${computedStyle.fontSize || '14px'} ${computedStyle.fontFamily || 'Arial'}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const lines = element.textContent.trim().split('\n');
    const lineHeight =
      parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;

    lines.forEach((line, index) => {
      ctx.fillText(line, offsetX + 5, offsetY + 5 + index * lineHeight);
    });
  }
};

// Capture ArcGIS graphics layers (polygons, etc.) - unchanged
const captureArcGISGraphics = async (ctx, container, containerRect) => {
  const graphicsSelectors = [
    '.esri-view-surface [data-surface-type="graphics"]',
    '.esri-graphics',
    '[class*="graphics-layer"]',
    '.esri-view .esri-view-surface > div',
  ];

  for (const selector of graphicsSelectors) {
    const elements = container.querySelectorAll(selector);
    for (const element of elements) {
      const canvases = element.querySelectorAll('canvas');
      const svgs = element.querySelectorAll('svg');

      for (const canvas of canvases) {
        await captureHighQualityElement(ctx, canvas, containerRect);
      }

      for (const svg of svgs) {
        await captureHighQualityElement(ctx, svg, containerRect);
      }
    }
  }
};

// Convert canvas to optimized base64 with adaptive quality
const canvasToOptimizedBase64 = async (canvas) => {
  return new Promise((resolve) => {
    let currentQuality = COMPRESSION_CONFIG.quality;

    const tryCompression = (quality, format) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            if (format === 'image/jpeg') {
              // Fallback to PNG if JPEG fails
              tryCompression(0.95, 'image/png');
            } else {
              resolve(null);
            }
            return;
          }

          const fileSizeKB = blob.size / 1024;

          // If adaptive quality is enabled and file is too large, reduce quality
          if (
            COMPRESSION_CONFIG.adaptiveQuality &&
            fileSizeKB > COMPRESSION_CONFIG.targetSizeKB &&
            quality > 0.5
          ) {
            const newQuality = Math.max(0.5, quality - 0.1);
            tryCompression(newQuality, format);
            return;
          }

          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        },
        format,
        quality
      );
    };

    // Start with preferred format and quality
    const preferredFormat = COMPRESSION_CONFIG.usePNG ? 'image/png' : COMPRESSION_CONFIG.format;
    tryCompression(currentQuality, preferredFormat);
  });
};

// Process and save screenshots with enhanced quality handling
const processAndSaveScreenshots = async (canvas, methodUsed) => {
  try {
    // Convert to base64 for backend
    const base64Data = await canvasToOptimizedBase64(canvas);

    if (!base64Data) {
      throw new Error('Failed to convert canvas to base64');
    }

    // Prepare payload for backend
    const timestamp = Date.now();
    const screenshotName = `arcgis-hq-screenshot-${methodUsed}-${timestamp}`;

    const saveImage = await saveFile({
      folderName: 'Property Layout',
      file: base64Data,
    });

    const payload = {
      screenshotArray: [
        {
          name: 'Property Layout',
          screenshot: saveImage?.documentId,
        },
      ],
    };

    // Save to backend
    const response = await saveScreenshots(payload);

    // Save screenshot ID to localStorage if available in response
    if (response) {
      const screenshotId = response?.recordId;
      setItem('screenshotId', screenshotId);
    }

    // Also save locally
    await downloadCanvasLocally(canvas, screenshotName);

    alert('High-quality screenshot saved successfully to both system and backend!');
  } catch (error) {
    console.error('Failed to process and save screenshots:', error);

    // Try to save locally as fallback
    try {
      await downloadCanvasLocally(canvas, `arcgis-hq-screenshot-fallback-${Date.now()}`);
      alert('High-quality screenshot saved locally (backend save failed)');
    } catch (localError) {
      console.error('Local save also failed:', localError);
      throw new Error('Failed to save screenshot both locally and to backend');
    }
  }
};

// Download canvas locally with quality preservation
const downloadCanvasLocally = async (canvas, filename) => {
  return new Promise((resolve, reject) => {
    const format = COMPRESSION_CONFIG.usePNG ? 'image/png' : COMPRESSION_CONFIG.format;
    const quality = COMPRESSION_CONFIG.usePNG ? undefined : COMPRESSION_CONFIG.quality;

    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error('Failed to create image blob'));
          return;
        }

        try {
          const fileSizeKB = blob.size / 1024;

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.${format.split('/')[1]}`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(url);
          resolve();
        } catch (error) {
          console.error('Local download failed:', error);
          reject(error);
        }
      },
      format,
      quality
    );
  });
};
